/**
 * @author Nihar<nihar.baishya@314ecorp.com>
 * @description utils for eligibility batch upload
 */
import _ from 'lodash';
import * as XLSX from 'xlsx';

import dateParser from 'lib/dateParser';
import AppEvents, { Events, Messages } from '@veritable/common/utils/appEvents';
import { EMPTY_LOCATION_VALUE } from '../metadata';
import { schemaKeyVsSubmitKeyMapping } from 'components/forms/schema/batchUploadHeaderMappingSchema';
import {
	BATCH_ENTITY_MAP_STATUS,
	BATCH_HEADER_MAPPING_REQUIRED_COLUMNS_COUNT,
	DEFAULT_ELIGIBILITY_SERVICE_TYPE_CODE,
	ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES,
	ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES,
} from 'app-constants/eligibility';
import { YEAR_MONTH_DATE_TIME_FORMAT, EMPTY_VALUE, YEAR_MONTH_DATE_TIME_FORMAT_WITH_TIMEZONE } from '@veritable/common/constants';

// max email list size
const maxEmailListSize = 20;

const batchUploadStepKeys = {
	upload: 0,
	headerMapping: 1,
	dataMapping: 2,
	dataSubmission: 3,
};

const batchUploadDataMappingStepKeys = {
	provider: 0,
	payer: 1,
	location: 2,
};

const batchUploadRequestKeys = {
	creationDate: 'creationDate',
	startDatetime: 'startDatetime',
	endDatetime: 'endDatetime',
	status: 'status',
	createdBy: 'createdBy',
};

const mappingEntityMeta = {
	providers: {
		key: 'providers',
		placeholder: 'Please select a provider',
		emptyText: 'No provider',
	},
	payers: {
		key: 'payers',
		placeholder: 'Please select a payer',
		emptyText: 'No payer',
	},
	locations: {
		key: 'locations',
		placeholder: 'Please select a location (optional)',
		emptyText: 'No location',
	},
	serviceTypes: {
		key: 'serviceTypes',
		placeholder: 'Please select service types',
	},
};

// export Button labels
const exportSectionLabels = {
	all: 'All',
	processed: 'Processed',
	Unprocessed: 'Unprocessed',
	failed: 'Failed',
	active: 'Active',
	inactive: 'Inactive',
	unknown: 'Unknown',
	successful: 'Successful',
};

// function to download file from base64 content
const downloadBase64File = (base64File?: string, fileName = 'file'): void => {
	if (base64File) {
		const mediaType = 'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,';
		const link = document.createElement('a');
		link.href = mediaType + base64File;
		link.download = `${_.replace(fileName, '.xlsx', '')}.xlsx`;
		link.textContent = 'Download file!';
		AppEvents.publish(Events.Loading, Messages.DownloadingFile);
		link.click();
	}
};

// function to prepare eligibility button label
const getRunEligibilityButtonLabel = (total: number, invalid: number): string => {
	return `(${total - invalid} transactions)`;
};

// function to get percentage completed for  batch

const batchCompletionPercentage = (total?: number, done?: number): number => {
	if (!done && !total) {
		return 0;
	}
	return _.round((done * 100) / total, 2);
};

// function to prepare responses of the batch request
const prepareBatchResponseTabs = (
	data: EligibilityBatchUpload.IBatchEligibilityResponseList,
	metaData: Metadata.IResponse,
): Record<string, EligibilityBatchUpload.IBatchResponseTabItem> => {
	let tabItems;
	if (data?.entityMap) {
		const providerData = _.map(data?.entityMap?.providersMap, (value, key) => ({
			key,
			label: key,
			value: _.get(metaData?.providerMetaData, value?.providerId)?.name || EMPTY_VALUE,
		}));

		const locationData = _.map(data?.entityMap?.locationsMap, (value, key) => ({
			key,
			label: key,
			value: _.get(metaData?.locationNames, [value?.locationId || ''], EMPTY_VALUE),
		}));

		const payerData = _.map(data?.entityMap?.payersMap, (value, key) => ({
			key,
			label: key,
			value: _.get(metaData?.allEligibilityPayersMeta, [value?.tenantpayerId || ''])?.name || EMPTY_VALUE,
		}));

		const hasNonEmptyLocationValues = _.some(locationData, (item) => item?.value !== EMPTY_VALUE);

		tabItems = [
			{
				key: 'ProvidersMap',
				label: 'Provider',
				content: {
					columns: [
						{ headerName: 'Uploaded Provider', field: 'label' },
						{ headerName: 'Veritable Provider', field: 'value' },
					],
					data: providerData,
				},
			},
			{
				key: 'payersMap',
				label: 'Payer',
				content: {
					columns: [
						{ headerName: 'Uploaded Payer', field: 'label' },
						{ headerName: 'Veritable Payer', field: 'value' },
					],
					data: payerData,
				},
			},
		];
		if (hasNonEmptyLocationValues) {
			tabItems.push({
				key: 'locationsMap',
				label: 'Service Location',
				content: {
					columns: [
						{ headerName: 'Uploaded Service Location', field: 'label' },
						{ headerName: 'Veritable Service Location', field: 'value' },
					],
					data: locationData,
				},
			});
		}
	}
	return _.keyBy(tabItems, 'label');
};

// custom function to get requests count
const getRequestsCount = (batchMeta: EligibilityBatchUpload.IBatchEligibilityResponseList, status: (string | number)[]): number => {
	return _.sum(_.values(_.pick(_.cloneDeep(batchMeta?.summary) || {}, status)));
};

// function to generate scheduled time options
const generateTimeSlots = (timezone: string): IDate[] => {
	const adjustedTime = dateParser().tz(timezone).add(10, 'minutes');
	// Round up to the next 30-minute interval
	let nextThirtyMinute = dateParser(adjustedTime).startOf('minute');
	if (nextThirtyMinute.minute() % 30 !== 0) {
		nextThirtyMinute = nextThirtyMinute.add(30 - (nextThirtyMinute.minute() % 30), 'minutes');
	}
	const endOfDay = dateParser().endOf('day'); // End of the current day
	const timeSlots: IDate[] = [];
	// Generate time slots every 30 minutes until the end of the day
	while (nextThirtyMinute.isBefore(endOfDay)) {
		timeSlots.push(nextThirtyMinute);
		nextThirtyMinute = nextThirtyMinute.add(30, 'minutes');
	}
	// Check the gap between the adjusted time and the end of the day,
	// if the gap is more than 0 minutes, then only show the last option
	if (endOfDay.diff(adjustedTime, 'minutes') >= 0) {
		timeSlots.push(nextThirtyMinute.subtract(1, 'minutes'));
	}

	return timeSlots;
};

// function to prepare scheduled time options

const prepareScheduledTimeOptions = (timezone: string): Record<'label' | 'value', string | number>[] => {
	const slots = generateTimeSlots(timezone);
	return _.map(slots, (slot) => {
		return {
			label: slot?.format('HH:mm'),
			value: slot?.tz(timezone)?.format(YEAR_MONTH_DATE_TIME_FORMAT_WITH_TIMEZONE),
		};
	});
};

// function to know if there is inprogress batch or scheduled batch

const hasIncompleteBatch = (
	data?: EligibilityBatchUpload.IBatchEligibilityRequests,
): Record<'hasInprogressBatch' | 'hasScheduledBatch', boolean> => {
	const hasInprogressBatch = !_.isEmpty(
		_.find(data?.data || [], (record) => record?.status === ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES.inProgress),
	);
	const hasScheduledBatch = !_.isEmpty(
		_.find(data?.data || [], (record) => record?.status === ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES.scheduled),
	);
	return { hasInprogressBatch, hasScheduledBatch };
};

// prepare provider mapping model for table
const prepareProviderTableData = (
	mapping: EligibilityBatchUpload.IBatchEntityMappingModel,
): EligibilityBatchUpload.IMappedProviderDetails[] => {
	const providers = mapping?.entityMap?.entities?.providersMap;
	const counts = mapping?.entityMap?.counts?.providersMap;

	const providerData = _.map(providers, (providerDetails, providerKey) => ({
		label: providerKey,
		providerId: providerDetails?.providerId || null,
		status: _.isNumber(providerDetails?.status) ? providerDetails?.status : BATCH_ENTITY_MAP_STATUS.mapped,
		count: _.get(counts, providerKey),
	}));
	return _.orderBy(providerData, ['count'], ['desc']);
};

// prepare location mapping model for table
const prepareLocationTableData = (
	mapping: EligibilityBatchUpload.IBatchEntityMappingModel,
): EligibilityBatchUpload.IMappedLocationDetails[] => {
	const locations = mapping?.entityMap?.entities?.locationsMap;
	const counts = mapping?.entityMap?.counts?.locationsMap;

	const locationData = _.map(locations, (locationDetails, locationKey) => ({
		label: locationKey,
		locationId: locationDetails?.locationId || null,
		status: _.isNumber(locationDetails?.status) ? locationDetails?.status : BATCH_ENTITY_MAP_STATUS.mapped,
		count: _.get(counts, locationKey),
	}));
	return _.orderBy(locationData, ['count'], ['desc']);
};

// get provider count per payer
const getProviderCountPerPayer = (mapping: EligibilityBatchUpload.IBatchEntityMappingModel, payerKey: string) => {
	const providers = mapping?.entityMap?.entities?.providersMap;
	const payerProviders = mapping?.entityMap?.counts?.payerProviders;
	const providerCountList = _.compact(
		_.map(payerProviders?.[payerKey] || {}, (count, providerName) => {
			const providerId = _.get(providers, providerName)?.providerId;
			return providerId ? { [providerId]: count } : null;
		}),
	);
	const aggregatedCounts = _.reduce(
		providerCountList,
		(result, obj) => {
			return _.mergeWith(result, obj, (objValue, srcValue) => {
				return _.isNumber(objValue) ? objValue + srcValue : srcValue;
			});
		},
		{},
	);

	return _.map(aggregatedCounts, (value, key) => ({ [key]: value }));
};

// prepare payer mapping model for table
const preparePayerTableData = (mapping: EligibilityBatchUpload.IBatchEntityMappingModel): EligibilityBatchUpload.IMappedPayerDetails[] => {
	const payers = mapping?.entityMap?.entities?.payersMap;
	const providers = mapping?.entityMap?.entities?.providersMap;
	const payerProviders = mapping?.entityMap?.counts?.payerProviders;
	const linkedProvidersList = _.mapValues(payerProviders, (providersObj) => _.keys(providersObj));
	const counts = mapping?.entityMap?.counts?.payersMap;

	const payerData = _.map(payers, (payerDetails, payerKey) => ({
		label: payerKey,
		tenantpayerId: payerDetails?.tenantpayerId || null,
		serviceType: !_.isEmpty(payerDetails?.serviceTypes) ? payerDetails?.serviceTypes : null,
		linkedProviders: _.compact(
			_.map(linkedProvidersList?.[payerKey] || [], (providerName) => _.get(providers, providerName)?.providerId),
		),
		linkedProvidersCount: getProviderCountPerPayer(mapping, payerKey),
		status: _.isNumber(payerDetails?.status) ? payerDetails?.status : BATCH_ENTITY_MAP_STATUS.mapped,
		count: _.get(counts, payerKey),
	}));
	return _.orderBy(payerData, ['count'], ['desc']);
};

// function to prepare initial mapping
const prepareInitialMapping = (batchData: EligibilityBatchUpload.IBatchPostResponseModel): EligibilityBatchUpload.InitialMappingMeta => {
	const mapping = _.cloneDeep(batchData);
	const providerMapping = mapping?.entityMap?.entities?.providersMap;
	const payerMapping = mapping?.entityMap?.entities?.payersMap;
	const locationMapping = mapping?.entityMap?.entities?.locationsMap;
	// Service Location is part of Data Mapping only when the user mapped the Service Location column during Header Mapping.
	// Deriving this solely from the current batch's header mapping ensures a previous batch's location mapping is never carried forward.
	const hideLocationMapping = _.isEmpty(mapping?.headerMap?.Location);
	const autoFilledDetails = {} as EligibilityBatchUpload.IAutoFilledMeta;

	mapping.entityMap.entities.providersMap = _.mapValues(providerMapping, (value) => {
		if (_.isEmpty(value)) {
			return {
				providerId: null,
				status: BATCH_ENTITY_MAP_STATUS.mapped,
			};
		} else {
			autoFilledDetails.providerMapping = true;
		}
		return value;
	});
	mapping.entityMap.entities.locationsMap = _.mapValues(locationMapping, (value) => {
		if (_.isEmpty(value)) {
			return {
				locationId: null,
				status: BATCH_ENTITY_MAP_STATUS.mapped,
			};
		} else {
			autoFilledDetails.locationMapping = true;
			return {
				locationId: value?.locationId || EMPTY_LOCATION_VALUE,
				status: hideLocationMapping ? BATCH_ENTITY_MAP_STATUS.mapped : value?.status,
			};
		}
	});
	mapping.entityMap.entities.payersMap = _.mapValues(payerMapping, (value) => {
		if (_.isEmpty(value)) {
			return {
				serviceTypes: [DEFAULT_ELIGIBILITY_SERVICE_TYPE_CODE],
				tenantpayerId: null,
				status: BATCH_ENTITY_MAP_STATUS.mapped,
			};
		} else {
			autoFilledDetails.payerMapping = true;
		}
		return value;
	});

	return {
		initialMapping: mapping as EligibilityBatchUpload.IBatchEntityMappingModel,
		autoFilledDetails,
		hideLocationMapping,
	};
};

// function to get enrollment details based on status
const prepareEnrollmentDetails = (data?: Payers.IProviderPayerMeta[]): EligibilityBatchUpload.IEnrollmentCategories => {
	const categories = {
		enrolledProviders: [] as Payers.IProviderPayerMeta[],
		enrollmentPendingProviders: [] as Payers.IProviderPayerMeta[],
		nonEnrolledProviders: [] as Payers.IProviderPayerMeta[],
		enrollmentRejectedProviders: [] as Payers.IProviderPayerMeta[],
	};
	_.forEach(data, (item) => {
		const { enrollmentStatus } = item;
		if (enrollmentStatus === ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.completed) {
			categories.enrolledProviders.push(item);
		} else if (
			enrollmentStatus === ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.pending ||
			enrollmentStatus === ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.requested
		) {
			categories.enrollmentPendingProviders.push(item);
		} else if (enrollmentStatus === ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.notEnrolled) {
			categories.nonEnrolledProviders.push(item);
		} else if (enrollmentStatus === ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.failed) {
			categories.enrollmentRejectedProviders.push(item);
		}
	});

	return categories;
};

// batch upload submit model
const prepareBatchUploadSubmitModel = (
	mapping: EligibilityBatchUpload.IBatchEntityMappingModel,
	scheduledTime?: string,
): EligibilityBatchUpload.IBatchEligibilityPatchModel => {
	const entityModel = _.cloneDeep(mapping?.entityMap?.entities);
	const submitModel = { batchId: mapping?.batchId } as EligibilityBatchUpload.IBatchEligibilityPatchModel;
	entityModel.payersMap = _.mapValues(entityModel?.payersMap, (item) => {
		if (_.isEmpty(item?.serviceTypes) && item?.status === BATCH_ENTITY_MAP_STATUS.mapped) {
			return {
				...item,
				serviceTypes: [DEFAULT_ELIGIBILITY_SERVICE_TYPE_CODE],
			};
		}
		return item;
	});
	entityModel.locationsMap = _.mapValues(entityModel?.locationsMap, (item) => {
		return {
			...item,
			locationId: item?.locationId === EMPTY_LOCATION_VALUE ? null : item?.locationId,
		};
	});
	submitModel.entityMap = entityModel;
	if (scheduledTime) {
		submitModel.schedule = scheduledTime;
	}
	return submitModel;
};

// function to get expired batch ids from persisted storage
const getExpiredBatchIDs = (persistedMappings: Record<string, EligibilityBatchUpload.IBatchEntityMappingModel>): string[] => {
	const expiredBatchIDs = _.map(persistedMappings, (mapping) => {
		if (!mapping.created) {
			return null;
		}
		const formattedDate = dateParser(mapping.created, YEAR_MONTH_DATE_TIME_FORMAT);
		const isOlderThan14Days = formattedDate.isBefore(dateParser().subtract(14, 'days'));
		return isOlderThan14Days ? mapping?.batchId : null;
	});
	return _.compact(expiredBatchIDs);
};

// highlight the table row after enrollment modal closes
const highlightRow = (rowKey: string): void => {
	const rows = document.querySelectorAll('.data-mapping-card-list-item');

	const targetRow = _.find(Array.from(rows), (row) => {
		const labelElement = row.querySelector('.uploaded-entity-label');
		return labelElement?.textContent?.includes(rowKey);
	});

	if (targetRow) {
		targetRow.classList?.add('highlight-row');
		setTimeout(() => {
			targetRow.classList?.remove('highlight-row');
		}, 3000);
	}
};

// file structural error messages
const FILE_STRUCTURAL_ERROR_MESSAGES = {
	multipleSheets: 'Your file contains multiple sheets. Please ensure that your file contains only one sheet.',
	missingRequiredColumns: 'Your file does not contain the required columns.',
	noData: 'Your file does not contain any data.',
	duplicateColumn: 'Duplicate column name:',
	maxRequestLimitExceeded: 'File contains more than 10,000 requests. Please reduce the number of requests and try again.',
};

// function to extract the file structure and prepare the structural error and column names

const getFileMeta = (file: RcFile, setValidationMeta: (meta: EligibilityBatchUpload.IBatchValidationMeta) => void): void => {
	const errorMessages = [] as string[];
	const reader = new FileReader();
	reader.onload = (e) => {
		try {
			const data = e?.target?.result;
			const workbook = XLSX.read(data, { type: 'binary' });

			const allSheets = workbook?.SheetNames;
			const firstSheetName = allSheets?.[0];
			const worksheet = workbook?.Sheets?.[firstSheetName];
			const range = XLSX.utils.decode_range(worksheet?.['!ref'] || 'A1');

			// Get column headers
			const columnNames = [];
			for (let C = 0; C <= range.e.c; C++) {
				const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
				const cell = worksheet?.[cellAddress];
				const trimmedColumnName = _.trim(_.toString(cell?.v || ''));
				if (!_.isEmpty(trimmedColumnName)) {
					if (!_.includes(columnNames, trimmedColumnName)) {
						columnNames.push(trimmedColumnName);
					} else {
						errorMessages.push(`${FILE_STRUCTURAL_ERROR_MESSAGES.duplicateColumn} ${trimmedColumnName}`);
					}
				}
			}

			// error checks
			if (allSheets.length > 1) {
				errorMessages.push(FILE_STRUCTURAL_ERROR_MESSAGES.multipleSheets);
			} else {
				if (columnNames.length < BATCH_HEADER_MAPPING_REQUIRED_COLUMNS_COUNT) {
					errorMessages.push(FILE_STRUCTURAL_ERROR_MESSAGES.missingRequiredColumns);
				}
				if (range.e.r === 0) {
					errorMessages.push(FILE_STRUCTURAL_ERROR_MESSAGES.noData);
				}
				if (range.e.r > 10000) {
					errorMessages.push(FILE_STRUCTURAL_ERROR_MESSAGES.maxRequestLimitExceeded);
				}
			}
			setValidationMeta({ errorMessages: _.uniq(errorMessages), columnNames });
		} catch (error) {
			setValidationMeta({ errorMessages: [JSON.stringify(error)] });
		}
	};

	reader.onerror = () => {
		setValidationMeta({ errorMessages: ['Error Reading the file'] });
	};

	reader.readAsArrayBuffer(file);
};

// function to move to top of the page
const scrollToTop = (): void => {
	const headerPage = document.querySelector('.ant-pro-page-container-children-container');
	if (headerPage) {
		headerPage?.scrollTo({ top: 0, behavior: 'auto' });
	}
};

// function to scroll to the bottom of the page
const scrollToBottom = (): void => {
	const headerPage = document.querySelector('.ant-pro-page-container-children-container');
	if (headerPage) {
		headerPage?.scrollTo({ top: headerPage.scrollHeight, behavior: 'auto' });
	}
};

// function to prepare header mapping submit model in required format
const prepareHeaderMappingSubmitModel = (
	formModel: Record<EligibilityBatchUpload.IHeaderMappingSchemaKeys, string | undefined>,
): EligibilityBatchUpload.IHeaderMappingSubmitModel => {
	return _.mapKeys(
		_.pickBy(formModel, _.identity),
		(__, key) => schemaKeyVsSubmitKeyMapping[key as EligibilityBatchUpload.IHeaderMappingSchemaKeys],
	) as EligibilityBatchUpload.IHeaderMappingSubmitModel;
};

// function to check if critical fields are changed
const isCriticalFieldChanged = (
	submitModel: EligibilityBatchUpload.IHeaderMappingSubmitModel,
	existingHeaderMapping?: EligibilityBatchUpload.IHeaderMappingSubmitModel,
): boolean => {
	const criticalFields = [schemaKeyVsSubmitKeyMapping.payer, schemaKeyVsSubmitKeyMapping.provider, schemaKeyVsSubmitKeyMapping.location];
	return _.some(criticalFields, (field) => _.get(submitModel, field) !== _.get(existingHeaderMapping, field));
};

const formatDate = (date: string): string => {
	const dateFormat = 'MMM D, YYYY [at] h:mma';
	if (_.isNil(date)) {
		return '';
	}

	// Create dateParser object from input (if it's not already a dateParser object)
	const parsedDate = dateParser(date);

	// Return empty string if invalid date
	if (!parsedDate.isValid()) {
		return '';
	}

	// Format the date using dateParser
	return parsedDate.format(dateFormat);
};
export {
	scrollToTop,
	mappingEntityMeta,
	maxEmailListSize,
	batchUploadRequestKeys,
	exportSectionLabels,
	batchUploadStepKeys,
	batchUploadDataMappingStepKeys,
	downloadBase64File,
	getRunEligibilityButtonLabel,
	prepareBatchResponseTabs,
	getRequestsCount,
	prepareScheduledTimeOptions,
	hasIncompleteBatch,
	batchCompletionPercentage,
	prepareProviderTableData,
	prepareLocationTableData,
	preparePayerTableData,
	prepareInitialMapping,
	prepareEnrollmentDetails,
	prepareBatchUploadSubmitModel,
	getExpiredBatchIDs,
	highlightRow,
	getFileMeta,
	prepareHeaderMappingSubmitModel,
	isCriticalFieldChanged,
	scrollToBottom,
	formatDate,
};
